/**
 * Surcouche d'arêtes 3D — plis internes + coutures entre parts ancrées.
 */
import * as THREE from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {
  ANCHOR_POINTS,
  convertLegacyAnchorId,
  getFaceDimensions,
  rotatePoint,
} from './anchorSystem';

/** Edges de contour sur chaque mesh (EdgesGeometry). */
export const EDGE_OVERLAY_ENABLED = true;
/** Lignes de couture entre parts ancrées (positions théoriques). Suspect "traits dans le vide". */
export const SEAM_LINES_ENABLED = false;

// Seuil d'angle dièdre (°) au-delà duquel une arête vive est surlignée.
// Élevé volontairement : on veut les coins francs du contour (~90°) sans les
// plis internes plus doux. À ajuster si des plis internes ressortent encore
// (monter le seuil) ou si du contour manque (descendre).
export const EDGE_THRESHOLD_ANGLE = 82;
/** Marge (mm) autour des zones à exclure (trous, zone motif). */
export const EDGE_ZONE_FILTER_MARGIN = 2;
export const EDGE_COLOR = 0x444444;
export const EDGE_OPACITY = 0.1;
export const SEAM_COLOR = 0x3a3a3a;
export const SEAM_OPACITY = 0.28;

const edgeMaterial = new THREE.LineBasicMaterial({
  color: EDGE_COLOR,
  transparent: true,
  opacity: EDGE_OPACITY,
  depthTest: true,
  depthWrite: false,
});

const seamMaterial = new THREE.LineBasicMaterial({
  color: SEAM_COLOR,
  transparent: true,
  opacity: SEAM_OPACITY,
  depthTest: true,
  depthWrite: false,
});

function createExplicitOuterContourLine(mesh) {
  const pts = mesh.userData?.outerContourPolyline;
  if (!Array.isArray(pts) || pts.length < 3) return null;

  // Le contour vient du DXF en coordonnées locales du mesh. On le dessine
  // directement, au lieu de demander à EdgesGeometry de deviner les arêtes :
  // cela évite tous les traits internes (trous, motifs, triangulation).
  const z = 0.03;
  const points = pts
    .filter((p) => Number.isFinite(p?.x) && Number.isFinite(p?.y))
    .map((p) => new THREE.Vector3(p.x, p.y, z));
  if (points.length < 3) return null;

  const line = new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints(points),
    edgeMaterial.clone(),
  );
  line.userData.isEdgeOverlay = true;
  line.renderOrder = (mesh.renderOrder || 0) + 2;
  return line;
}

/** Mesh ou sous-arbre à ne pas surligner (flaps cutout, motif OBJ, etc.). */
function isExcludedFromEdgeOverlay(obj) {
  let cur = obj;
  while (cur) {
    if (cur.userData?.isCutoutFlap) return true;
    if (cur.userData?.isObjPatternRepeat) return true;
    // Tout descendant d'un groupe de motif incrusté (repeat OU crop) : on ne
    // veut pas surligner le motif lui-même, seulement le contour de la part.
    if (cur.name === 'objPattern') return true;
    cur = cur.parent;
  }
  return false;
}

/**
 * Boîtes englobantes (XY, espace local du mesh) des zones d'incrustation motif.
 *
 * La face est extrudée d'un `THREE.Shape` dont la zone motif est un TROU. Le
 * bord de ce trou est une arête vive (~90°) → `EdgesGeometry` le surligne « tout
 * autour de la zone d'incrustation ». Le groupe `objPattern` est un enfant du
 * mesh face et occupe exactement cette zone : sa bbox nous donne la zone à
 * exclure du surlignage.
 */
function collectMotifZoneBoxes(mesh) {
  const boxes = [];
  mesh.updateWorldMatrix(true, true);
  const corner = new THREE.Vector3();
  mesh.traverse((node) => {
    if (node === mesh) return;
    if (node.name !== 'objPattern') return;
    const worldBox = new THREE.Box3().setFromObject(node);
    if (worldBox.isEmpty()) return;
    const localBox = new THREE.Box3();
    for (let i = 0; i < 8; i++) {
      corner.set(
        i & 1 ? worldBox.max.x : worldBox.min.x,
        i & 2 ? worldBox.max.y : worldBox.min.y,
        i & 4 ? worldBox.max.z : worldBox.min.z,
      );
      mesh.worldToLocal(corner);
      localBox.expandByPoint(corner);
    }
    boxes.push(localBox);
  });

  // Motifs incrustés en 2D dans la face (crop/scale/repeat DXF) : pas de groupe
  // `objPattern` 3D, le motif est fondu dans la géométrie de la face. L'OTS
  // remonte alors les bboxes des zones (coords DXF = espace local du mesh) dans
  // `userData.patternZones` → on les ajoute pour exclure motif ET contour de zone.
  const zoneList =
    mesh.userData?.edgeExclusionZones ?? mesh.userData?.patternZones;
  if (Array.isArray(zoneList)) {
    for (const z of zoneList) {
      if (!z) continue;
      boxes.push(zoneToBox3(z));
    }
  }

  return boxes;
}

function zoneToBox3(z) {
  const m = EDGE_ZONE_FILTER_MARGIN;
  return new THREE.Box3(
    new THREE.Vector3(z.minX - m, z.minY - m, -Infinity),
    new THREE.Vector3(z.maxX + m, z.maxY + m, Infinity),
  );
}

/**
 * Retire les segments d'arêtes liés aux trous / zones motif sur la face.
 * Un trou perçé ou un motif en « trou » crée un contour interne à ~90° que
 * EdgesGeometry surligne. On exclut tout segment dont une extrémité ou le
 * milieu tombe dans une zone d'exclusion (pas seulement les deux bouts).
 */
function filterEdgesOutsideZones(edgesGeom, boxes) {
  if (!boxes.length) return edgesGeom;
  const pos = edgesGeom.getAttribute('position');
  if (!pos) return edgesGeom;

  const inAnyZone = (x, y) =>
    boxes.some(
      (b) => x >= b.min.x && x <= b.max.x && y >= b.min.y && y <= b.max.y,
    );

  const kept = [];
  for (let i = 0; i < pos.count; i += 2) {
    const ax = pos.getX(i), ay = pos.getY(i), az = pos.getZ(i);
    const bx = pos.getX(i + 1), by = pos.getY(i + 1), bz = pos.getZ(i + 1);
    const mx = (ax + bx) * 0.5;
    const my = (ay + by) * 0.5;
    if (inAnyZone(ax, ay) || inAnyZone(bx, by) || inAnyZone(mx, my)) continue;
    kept.push(ax, ay, az, bx, by, bz);
  }

  if (kept.length === pos.count * 3) return edgesGeom;
  const filtered = new THREE.BufferGeometry();
  filtered.setAttribute('position', new THREE.Float32BufferAttribute(kept, 3));
  edgesGeom.dispose();
  return filtered;
}

/**
 * Construit une géométrie indexée fusionnée PAR POSITION pour le calcul des arêtes.
 *
 * `EdgesGeometry` détecte les arêtes vives en comparant les normales des deux
 * faces qui partagent une arête. Or les GLTF exportés ont des sommets
 * dupliqués / non indexés (chaque triangle a ses propres sommets, normales et
 * UV distincts aux coutures). Résultat : aucune arête n'est « partagée », donc
 * `EdgesGeometry` les traite TOUTES comme des arêtes de bord → un trait par
 * arête de triangle (les « traits parasites »).
 *
 * On reconstruit donc une géométrie ne contenant que la `position`, puis on
 * fusionne les sommets coïncidents (`mergeVertices`). Les faces adjacentes
 * partagent alors leurs sommets et l'angle dièdre est calculé correctement —
 * comme dans un viewer classique. On retire `normal`/`uv` exprès pour que la
 * fusion se fasse uniquement sur la position (sinon des sommets identiques mais
 * de normale différente ne fusionneraient pas).
 */
function buildPositionMergedGeometry(geometry) {
  const position = geometry.getAttribute('position');
  if (!position) return null;

  const posOnly = new THREE.BufferGeometry();
  posOnly.setAttribute('position', position.clone());
  if (geometry.index) posOnly.setIndex(geometry.index.clone());

  try {
    // Tolérance un peu plus large (mm) : fusionne mieux les sommets du GLTF
    // et évite des arêtes vives fantômes entre triangles coplanaires.
    return mergeVertices(posOnly, 0.05);
  } catch {
    // Fallback défensif : si la fusion échoue, on retombe sur la géométrie brute.
    return posOnly;
  }
}

/**
 * Ajoute des LineSegments sur chaque mesh de l'arbre.
 *
 * On vise les arêtes EXTERNES (contour) de la part sans les plis internes.
 * `THREE.EdgesGeometry` garde les arêtes de bord ET les arêtes vives dont
 * l'angle dièdre dépasse `thresholdAngle`. La géométrie est d'abord fusionnée
 * par position (cf. `buildPositionMergedGeometry`) pour éviter de surligner
 * chaque arête de triangle.
 */
export function addMeshEdgeLines(root, {
  thresholdAngle = EDGE_THRESHOLD_ANGLE,
  skipExcludedMeshes = true,
} = {}) {
  if (!EDGE_OVERLAY_ENABLED) return;
  root.traverse((child) => {
    if (!child.isMesh || !child.geometry) return;
    if (skipExcludedMeshes && isExcludedFromEdgeOverlay(child)) return;
    if (child.userData?.isEdgeOverlay) return;

    const explicitContour = createExplicitOuterContourLine(child);
    if (explicitContour) {
      child.add(explicitContour);
      return;
    }

    const mergedGeom = buildPositionMergedGeometry(child.geometry);
    if (!mergedGeom) return;

    let edgesGeom = new THREE.EdgesGeometry(mergedGeom, thresholdAngle);
    mergedGeom.dispose();

    // Supprime le contour du trou de la zone motif (le motif est un enfant du
    // mesh, sa bbox délimite la zone à ne pas surligner).
    const zoneBoxes = collectMotifZoneBoxes(child);
    edgesGeom = filterEdgesOutsideZones(edgesGeom, zoneBoxes);

    const lines = new THREE.LineSegments(edgesGeom, edgeMaterial.clone());
    lines.userData.isEdgeOverlay = true;
    lines.renderOrder = (child.renderOrder || 0) + 2;
    child.add(lines);
  });
}

/**
 * Extrémités locales du segment de couture sur la face d'ancrage d'une part.
 */
export function getSeamLineLocalEndpoints(pointId, dims) {
  const id = convertLegacyAnchorId(pointId);
  const anchor = ANCHOR_POINTS[id];
  if (!anchor) return null;

  const w = dims.width || 0;
  const h = dims.height || 0;
  const d = dims.depth || 0;
  const bc = dims.bboxCenter || { x: 0, y: 0, z: 0 };
  const hw = w / 2;
  const hh = h / 2;
  const hd = d / 2;
  const [ax, ay, az] = anchor.position;

  const cx = bc.x + ax * w;
  const cy = bc.y + ay * h;
  const cz = bc.z + az * d;

  const eps = 0.49;
  if (Math.abs(ax) >= eps && Math.abs(ay) < eps && Math.abs(az) < eps) {
    return [
      { x: cx, y: cy - hh, z: cz },
      { x: cx, y: cy + hh, z: cz },
    ];
  }
  if (Math.abs(ay) >= eps && Math.abs(ax) < eps && Math.abs(az) < eps) {
    return [
      { x: cx - hw, y: cy, z: cz },
      { x: cx + hw, y: cy, z: cz },
    ];
  }
  if (Math.abs(az) >= eps && Math.abs(ax) < eps && Math.abs(ay) < eps) {
    return [
      { x: cx, y: cy - hh, z: cz },
      { x: cx, y: cy + hh, z: cz },
    ];
  }
  if (Math.abs(ax) >= eps && Math.abs(az) >= eps && Math.abs(ay) < eps) {
    return [
      { x: cx, y: cy - hh, z: cz },
      { x: cx, y: cy + hh, z: cz },
    ];
  }
  if (Math.abs(ax) >= eps && Math.abs(ay) >= eps) {
    return [
      { x: cx, y: cy, z: cz - hd },
      { x: cx, y: cy, z: cz + hd },
    ];
  }
  if (Math.abs(ay) >= eps && Math.abs(az) >= eps) {
    return [
      { x: cx - hw, y: cy, z: cz },
      { x: cx + hw, y: cy, z: cz },
    ];
  }
  return null;
}

/**
 * Ligne de couture locale (dans le repère du group de la part).
 */
export function createPartSeamLine(part, dims) {
  if (!SEAM_LINES_ENABLED) return null;
  if (!part?.anchor?.myPoint) return null;

  const endpoints = getSeamLineLocalEndpoints(part.anchor.myPoint, dims);
  if (!endpoints) return null;

  const rot = {
    x: part.rotationX ?? 0,
    y: part.rotationY ?? 0,
    z: part.rotationZ ?? 0,
  };
  const p1 = rotatePoint(endpoints[0], rot);
  const p2 = rotatePoint(endpoints[1], rot);

  const geometry = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(p1.x, p1.y, p1.z),
    new THREE.Vector3(p2.x, p2.y, p2.z),
  ]);
  const line = new THREE.Line(geometry, seamMaterial.clone());
  line.userData.isEdgeOverlay = true;
  line.renderOrder = 10;
  return line;
}

/**
 * Résout les dimensions locales d'une part (mesh réel ou théorique).
 */
export function getPartDims(part, realMeshSizes, productSize) {
  const pid = String(part.clientId || part.id);
  if (realMeshSizes?.[pid]) {
    const m = realMeshSizes[pid];
    return {
      width: m.width,
      height: m.height,
      depth: m.depth,
      bboxCenter: m.bboxCenter || { x: 0, y: 0, z: 0 },
    };
  }
  return getFaceDimensions(part, productSize);
}
